<template>
    <CqModal
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Opmerking bewerken"
    >
        <InfoGrid>
            <InfoHeader>Medewerker</InfoHeader><div><template v-if="employment">{{ ResourceUtil.displayName(employment.resources![0]) }}</template></div>
            <template v-if="date">
                <InfoHeader>Datum</InfoHeader><div>{{ formattedDate }}</div>
            </template>
        </InfoGrid>
        <CqTextAreaInput v-model="commentContent" @update:modelValue="onChangeValue" label="Opmerking" :invalid="error" :invalidFeedback="errorLabel" />
        <CqButtonBar @click.stop>
            <CqButton :disabled="isSaving" label="Annuleren" @click="closeModal" />
            <CqButton :disabled="!valueIsValid || isSaving" label="Opslaan" icon="save" primary @click="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqTextAreaInput} from '@cquential/ui';
    import {ApiClient, EmploymentComment, RosterEmployment} from '@cquential/api';
    import Dates from '@cquential/date-utils';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import InfoGrid from '@/components/Branding/InfoGrid.vue';
    import InfoHeader from '@/components/Branding/InfoHeader.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqTextAreaInput,
            InfoGrid,
            InfoHeader,
        },
        emits: ['save:employmentComment'],
    })
    export default class EmploymentCommentModal extends Vue {
        private static instance: EmploymentCommentModal | null;

        public show: boolean = false;
        public employment: RosterEmployment | null = null;
        public date: Date | null = null;
        public error: boolean = false;
        public errorLabel: string = '';
        public comment: EmploymentComment | null = null;
        public commentContent: string = '';
        public ResourceUtil: typeof ResourceUtil = ResourceUtil;
        public isSaving: boolean = false;

        public mounted(): void {
            EmploymentCommentModal.instance = this;
        }

        public beforeUnmount(): void {
            EmploymentCommentModal.instance = null;
        }

        public get formattedDate(): string {
            if (this.date) {
                return Dates.Formatter.formatDateHumanFriendly(this.date);
            }

            return '';
        }

        public get valueIsValid(): boolean {
            if (this.comment) {
                return this.comment.content.length < 1024;
            }

            return true;
        }

        public openModal(rosterEmployment: RosterEmployment, commentDate?: Date, commentId?: number): void {
            if (rosterEmployment.id) {
                this.error = false;
                this.errorLabel = '';
                this.show = true;

                this.employment = rosterEmployment;
                this.date = commentDate ?? null;
                this.comment = null;

                const employmentComment: EmploymentComment = {
                    id: commentId,
                    employmentId: rosterEmployment.id,
                    date: commentDate,
                    content: '',
                };

                if (employmentComment.date && rosterEmployment.commentsByDate) {
                    const employmentCommentForDate: EmploymentComment | undefined = rosterEmployment.commentsByDate[Dates.Formatter.formatDateComputerFriendly(employmentComment.date)];
                    if (employmentCommentForDate) {
                        employmentComment.id = employmentCommentForDate.id;
                        employmentComment.content = employmentCommentForDate.content;
                    }
                } else {
                    employmentComment.content = rosterEmployment.comment?.content ?? '';
                }

                this.comment = employmentComment;
                this.commentContent = employmentComment.content;
            }
        }

        private onChangeValue(): void {
            this.error = !this.valueIsValid;
            this.errorLabel = this.error ? 'Tekst te lang' : '';
        }

        private save(): void {
            if (this.isSaving || !this.valueIsValid || !(this.comment && (this.comment.id || this.commentContent.length > 0))) {
                return;
            }
            const employmentComment: EmploymentComment = {
                id: this.comment.id,
                employmentId: this.comment.employmentId,
                date: this.comment.date ? this.comment.date : undefined,
                content: this.commentContent,
            };
            ApiClient.instance.saveEmploymentComment(employmentComment).then((result: void | number): void => {
                if (result) {
                    employmentComment.id = result;
                }
                this.comment = employmentComment;
                this.$emit('save:employmentComment', this.comment);
                this.closeModal();
            }).catch((): void => {
                this.error = true;
                this.errorLabel = 'Kon de wijziging niet opslaan. Probeer het opnieuw.';
            }).finally((): void => {
                this.isSaving = false;
            });
        }

        public closeModal(): void {
            this.show = false;
        }

        public static openModal(rosterEmployment: RosterEmployment, commentDate?: Date, commentId?: number): void {
            this.instance!.openModal(rosterEmployment, commentDate, commentId);
        }
    }
</script>
